// MobileUploadPage.jsx
import { useState } from "react"
import { useParams } from "react-router-dom"
import { Camera, Upload, CheckCircle, ArrowLeft, Smartphone } from "lucide-react"
import toast from "react-hot-toast"

export default function MobileUploadPage() {
  const params = useParams()
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)

  const sessionId = params.sessionId
  const documentType = decodeURIComponent(params.documentType)

  const handleFileUpload = async (files) => {
    if (!files) return

    setIsUploading(true)

    try {
      const fileArray = Array.from(files)
      const formData = new FormData()
      formData.append("files", fileArray[0])
      formData.append("sessionId", sessionId)
      formData.append("documentType", documentType)

      const response = await fetch(window.ajaxLink + "cloud_dashboard/uploadTemporarilyPatientRegisterFiles", {
        method: "POST",
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Failed to upload ${files?.files?.name}`)
      }

      setUploadedFiles((prev) => [...prev, ...fileArray])
      setUploadSuccess(true)

    } catch (error) {
      console.error("Upload failed:", error)
      toast.error("Error occurred. Please try again.")
    } finally {
      setIsUploading(false)
    }
  }

  const handleCameraCapture = () => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.capture = "environment"
    input.multiple = false
    input.onchange = (e) => {
      const target = e.target
      handleFileUpload(target.files)
    }
    input.click()
  }

  const handleFileSelect = () => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.multiple = false
    input.onchange = (e) => {
      const target = e.target
      handleFileUpload(target.files)
    }
    input.click()
  }

  const goBack = () => {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      window.location.href = "/"
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-[#166273]/10 p-4">
      <div className="max-w-md mx-auto">

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-[#166273] to-[#166273]/90 px-6 py-6 text-center">
            <h1 className="text-xl font-bold text-white mb-2">Upload {documentType}</h1>
            <p className="text-blue-100 text-sm">Take photos or select files from your device</p>
          </div>

          <div className="p-6 space-y-4">

            <div className="space-y-3">
              <button
                onClick={handleCameraCapture}
                disabled={isUploading}
                className="w-full flex items-center justify-center gap-3 h-16 px-4 py-2 bg-[#166273] hover:bg-[#166273]/90 disabled:bg-[#166273]/80 text-white rounded-lg font-medium transition-colors"
              >
                <Camera className="w-6 h-6" />
                {isUploading ? "Processing..." : "Take Photo"}
              </button>

              <button
                onClick={handleFileSelect}
                disabled={isUploading}
                className="w-full flex items-center justify-center gap-3 h-16 px-4 py-2 border-2 border-[#166273]/60 hover:border-[#166273]/50 hover:bg-[#166273]/10 disabled:border-gray-200 disabled:bg-gray-50 text-[#166273] rounded-lg font-medium transition-colors"
              >
                <Upload className="w-6 h-6" />
                {isUploading ? "Processing..." : "Choose from Files"}
              </button>
            </div>

            {isUploading && (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-sm text-gray-600 mt-2">Uploading your files...</p>
              </div>
            )}

            {uploadedFiles.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3">Uploaded Files:</h4>
                <div className="space-y-2">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-3 rounded border">
                      <div>
                        <div className="font-medium text-gray-800 text-sm">{file.name}</div>
                        <div className="text-gray-500 text-xs">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                      </div>
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-center pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Session ID: {sessionId}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
